import type { AccessLevelProp, CSS, GutterConfig, GutterEdge, SpacingScale } from '@meterup/atto';
import type { ColumnFiltersState } from '@tanstack/table-core';
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import {
  Button,
  ClearAccessLevelContext,
  colors,
  ControlGroup,
  darkThemeSelector,
  Divider,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  getResizeCursor,
  gutterCSS,
  HStack,
  Icon,
  LoadingIcon,
  overflowFadeMaskCSS,
  ResizeCursorShield,
  sizing,
  styled,
  Text,
  useUtilityVisibility,
  UtilityControl,
} from '@meterup/atto';
import { useMeasure } from '@uidotdev/usehooks';
import { isNumber } from 'lodash-es';
import { useCallback, useEffect, useEffectEvent, useId, useMemo, useRef, useState } from 'react';

import type {
  FilterComponentFunction,
  FilterComponentProps,
  FilterOption,
  FilterOptions,
  FilterValue,
  FilterVariant,
  NormalizedFilterOption,
  SharedColumnDef,
} from '../createColumnBuilder';
import type { AutoCollectionStateInterface, Filter, Table } from '../utils';
import type { TableFiltersActions } from './TableFiltersActions';
import type { TableFiltersTokenProps } from './TableFiltersToken';
import { isDefined } from '../../../helpers/isDefined';
import { sortingCollator } from '../../../helpers/utils/strings';
import { parseCalendarDateRange, parseDateTime } from '../../../topics/time';
import { DateTimeRangeStorageProvider } from '../../DateTimeRangePicker/DateTimeRangeStorageContext';
import { TimePeriod } from '../../DateTimeRangePicker/utils';
import { FormatDateTime } from '../../FormatDateTime';
import { KeyBoundSearchInput } from '../../KeyBoundSearchInput';
import { buildColumnMenu } from '../TableColumns/columnMenu';
import { TableColumnsEnhanced } from '../TableColumns/TableColumns';
import { TablePresets } from '../TablePresets/TablePresets';
import { isTableSortOrder, TableFilterVariant, TableSortOrderType } from '../utils';
import { TableFiltersActionsMenu } from './TableFiltersActions';
import { useTableFiltersEnvironment } from './TableFiltersEnvironment';
import {
  TableFiltersFieldDateRange,
  TableFiltersFieldMultiSelect,
  TableFiltersFieldNumber,
  TableFiltersFieldNumberRange,
  TableFiltersFieldSelect,
  TableFiltersFieldText,
} from './TableFiltersField/TableFiltersField';
import { ServerTableFiltersToken, TableFiltersToken } from './TableFiltersToken';
import { useTableExport } from './useTableExport';
import {
  isFilterApplied,
  isNegatedFilterValue,
  multiSelectFilterValue,
  parseMultiSelectFilterValue,
} from './utils';

const tableFiltersVars = {
  padding: {
    sides: 8,
    ends: 6,
  },
} satisfies {
  padding: {
    sides: SpacingScale;
    ends: SpacingScale;
  };
};
const DEFAULT_OUTER_GUTTER = { context: 'application' } satisfies GutterConfig;

// Steps from 3.5 to 5.5 rows as the viewport gets taller. A row is a small token plus the gap (32px);
// the extra 16px is the top padding plus a 10px peek of the next row.
const NARROW_OPTIONS_MAX_HEIGHT =
  'calc(round(down, clamp(96px, 16svh - 16px, 160px), 32px) + 16px)';
const NARROW_OPTIONS_ROW_HEIGHT = 32;
const NARROW_OPTIONS_MIN_HEIGHT = NARROW_OPTIONS_ROW_HEIGHT + 16;

const outerGutterCSS = (
  edge: Extract<GutterEdge, 'start' | 'end'>,
  gutter: GutterConfig = DEFAULT_OUTER_GUTTER,
): CSS => {
  if (gutter.edge === 'none') {
    return {};
  }

  return gutterCSS({ ...gutter, edge, placement: 'outside' });
};

const narrowOptionsCSS = (gutter: GutterConfig | undefined): CSS => ({
  ...outerGutterCSS('start', gutter),
  ...outerGutterCSS('end', gutter),
});

// Watches content too: at max height, added filters grow scrollHeight without resizing the box.
function useOptionsScroll() {
  const elementRef = useRef<HTMLElement | null>(null);
  const [metrics, setMetrics] = useState({ height: 0, scrollHeight: 0, clientHeight: 0 });
  const metricsRef = useRef(metrics);
  const ref = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;
    if (!element) return undefined;
    const update = () => {
      const { scrollHeight, clientHeight } = element;
      const { height } = element.getBoundingClientRect();
      const prev = metricsRef.current;
      // Bail before setState: a same-value update can still schedule a render.
      if (
        prev.height === height &&
        prev.scrollHeight === scrollHeight &&
        prev.clientHeight === clientHeight
      ) {
        return;
      }
      metricsRef.current = { height, scrollHeight, clientHeight };
      setMetrics(metricsRef.current);
    };
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true, subtree: true, characterData: true });
    return () => {
      elementRef.current = null;
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
  return {
    height: metrics.height,
    scrollHeight: metrics.scrollHeight,
    overflowing: metrics.scrollHeight > metrics.clientHeight,
    elementRef,
    ref,
  };
}

interface SelectedFiltersDropdownOption {
  value: string;
  label: string;
  group?: string | null;
  accessLevel?: AccessLevelProp | null | undefined;
}

interface SelectedFiltersDropdownProps {
  options: Array<SelectedFiltersDropdownOption>;
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  /** The table's column order, which the options follow unless `sortOptions` is set. */
  columnOrder?: string[];
  onClearFilters?: () => void;
  loading?: boolean;
  /** When true, sort options alphabetically by label instead of by column order. */
  sortOptions?: boolean;
  disabled?: boolean;
}

const DEFAULT_FILTER_GROUP_NAME = 'Filters';
// A filter opened from a link can hold dozens of values, more than a token can show.
const MAX_TOKEN_VALUES = 3;

function moreValuesLabel(total: number) {
  return total > MAX_TOKEN_VALUES ? `& ${total - MAX_TOKEN_VALUES} more` : null;
}

// Isolates each nested group so it skips the divider atto draws between sibling groups, matching
// the columns menu.
const SelectedFiltersDropdownNestedGroup = styled('div', {});
SelectedFiltersDropdownNestedGroup.displayName = 'SelectedFiltersDropdownNestedGroup';

function getColumnFilterLabel<RowType>(col: SharedColumnDef<RowType, any>): string | undefined {
  if (col.filterLabel) return col.filterLabel;
  if (col.meta?.label) return col.meta.label;
  return typeof col.header === 'string' ? col.header : undefined;
}

function DisplayForOption({ option }: { option: FilterOption }) {
  return option.label ?? option.textValue ?? option.value;
}

function DisplayForFilterValue({
  value,
  options,
  variant,
  loading,
}: {
  value: FilterValue;
  options?: FilterOption[];
  variant?: FilterVariant;
  loading?: boolean;
}): ReactNode {
  if (loading) {
    return (
      <HStack role="status" aria-label="Loading filter options">
        <LoadingIcon size={16} />
      </HStack>
    );
  }

  if (isNegatedFilterValue(value)) {
    return <DisplayForFilterValue value={value.not} options={options} variant={variant} />;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (!value) {
    return 'All';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'All';
    }

    if (variant === 'calendar-date-range') {
      const [from, to] = parseCalendarDateRange(value);
      if (!from && !to) return null;
      return (
        <HStack align="center" spacing={4} wrap="nowrap">
          {from && !to && <Text>From</Text>}
          {!from && to && <Text>Through</Text>}
          {from && <FormatDateTime dateOnly value={from} />}
          {from && to && <Icon icon="arrow-right" size={10} />}
          {to && <FormatDateTime dateOnly value={to} />}
        </HStack>
      );
    }

    if (options) {
      const selectedOptions = value
        .map((v) => options.find((opt) => opt.value === v))
        .filter(isDefined);
      if (!selectedOptions.length) return '(Unknown option)';

      const shownOptions = selectedOptions.slice(0, MAX_TOKEN_VALUES);
      const more = moreValuesLabel(selectedOptions.length);
      return (
        <HStack spacing={4}>
          {shownOptions.map((option, index) => (
            <HStack key={option.value} wrap="nowrap" align="center">
              <DisplayForOption option={option} />
              {index < shownOptions.length - 1 ? <Text>,</Text> : null}
            </HStack>
          ))}
          {more && <Text>{more}</Text>}
        </HStack>
      );
    }

    if (variant !== 'multi-select') {
      if (value[0] === null && value[1] === null) {
        return null;
      }
      if (isNumber(value[0]) || isNumber(value[1])) {
        return (
          <HStack align="center" spacing={4} wrap="nowrap">
            {value[0]}
            <Icon icon="arrow-right" size={10} />
            {value[1]}
          </HStack>
        );
      }

      const startDate = parseDateTime(value[0]);
      const endDate = parseDateTime(value[1]);
      if (startDate || endDate) {
        return (
          <HStack align="center" spacing={4} wrap="nowrap">
            <FormatDateTime value={startDate} />
            <Icon icon="arrow-right" size={10} />
            <FormatDateTime value={endDate} />
          </HStack>
        );
      }
    }

    const more = moreValuesLabel(value.length);
    const shown = value.slice(0, MAX_TOKEN_VALUES).join(', ');
    return more ? `${shown} ${more}` : shown;
  }

  if (options) {
    const option = options.find((opt) => opt.value === value);
    if (option) {
      return <DisplayForOption option={option} />;
    }

    return null;
  }

  return value;
}

function SelectedFiltersDropdown({
  options,
  selectedFilters,
  setSelectedFilters,
  columnOrder,
  onClearFilters,
  loading,
  sortOptions,
  disabled,
}: SelectedFiltersDropdownProps) {
  const { items } = useMemo(() => {
    const order = sortOptions
      ? [...options].sort((a, b) => a.label.localeCompare(b.label)).map((opt) => opt.value)
      : columnOrder?.flatMap((id) => [id, `${id}-subline`]) ?? options.map((opt) => opt.value);

    return buildColumnMenu(
      options.map((option) => ({ id: option.value, meta: { group: option.group }, option })),
      order,
      () => false,
    );
  }, [options, columnOrder, sortOptions]);

  const renderOption = (opt: SelectedFiltersDropdownOption) => {
    const isSelected = selectedFilters.includes(opt.value);
    return (
      <DropdownMenuCheckboxItem
        key={opt.value}
        checked={isSelected}
        // Adding a filter auto-opens its token popover, so close rather than stack.
        closeOnClick={!isSelected}
        onCheckedChange={(checked) => {
          const newVal = checked
            ? [...selectedFilters, opt.value]
            : selectedFilters.filter((selected) => selected !== opt.value);
          setSelectedFilters(newVal);
        }}
        accessLevel={opt.accessLevel}
      >
        {opt.label}
      </DropdownMenuCheckboxItem>
    );
  };

  return (
    <DropdownMenu disabled={disabled}>
      <DropdownMenuButton
        tabIndex={0}
        appearance="muted"
        arrangement="hidden-label"
        icon="filter"
        size="small"
        loading={loading}
        disabled={disabled}
      >
        Add a filter...
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" hasInnerScroll>
        <ClearAccessLevelContext>
          <DropdownMenuGroup label={DEFAULT_FILTER_GROUP_NAME} scrollable>
            {items.map((item) =>
              item.type === 'column' ? (
                renderOption(item.column.option)
              ) : (
                <SelectedFiltersDropdownNestedGroup key={item.id}>
                  <DropdownMenuGroup isNested label={item.name}>
                    {item.columns.map((col) => renderOption(col.option))}
                  </DropdownMenuGroup>
                </SelectedFiltersDropdownNestedGroup>
              ),
            )}
          </DropdownMenuGroup>
        </ClearAccessLevelContext>
        {onClearFilters && (
          <DropdownMenuGroup>
            <DropdownMenuItem
              icon="trash-can"
              onClick={onClearFilters}
              disabled={selectedFilters.length === 0}
              tooltip={selectedFilters.length === 0 ? 'No filters are set' : undefined}
            >
              Clear filters
            </DropdownMenuItem>
          </DropdownMenuGroup>
        )}
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

const TableFiltersDivider = styled(Divider, {
  padding: '$4 $2',
});
TableFiltersDivider.displayName = 'TableFiltersDivider';

const TableFiltersViews = styled(ControlGroup, {
  gridArea: 'views',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  padding: `$${tableFiltersVars.padding.ends} $${tableFiltersVars.padding.sides / 2}`,
});
TableFiltersViews.displayName = 'TableFiltersViews';

const TableFiltersOptionsScroll = styled(
  'div',
  overflowFadeMaskCSS(`$${tableFiltersVars.padding.ends}`, 'all'),
  {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$8',
    maxWidth: '100%',
    overflow: 'auto',
    padding: `$${tableFiltersVars.padding.ends} ${sizing.sides}`,

    variants: {
      hasControls: {
        true: {},
        false: {},
      },
      hasViews: {
        true: {},
        false: {},
      },
      isNarrow: {
        true: {
          flexWrap: 'wrap',
          paddingX: 0,
          maxHeight: NARROW_OPTIONS_MAX_HEIGHT,
        },
        false: {},
      },
    },

    compoundVariants: [
      {
        hasControls: true,
        isNarrow: false,
        css: {
          paddingRight: `$${tableFiltersVars.padding.sides / 2}`,
        },
      },
      {
        hasViews: true,
        isNarrow: false,
        css: {
          paddingLeft: `$${tableFiltersVars.padding.sides / 2}`,
        },
      },
    ],
  },
);
TableFiltersOptionsScroll.displayName = 'TableFiltersOptionsScroll';

const TableFiltersOptions = styled('div', {
  gridArea: 'options',
  position: 'relative',
  maxWidth: '100%',
  minWidth: 0,
});
TableFiltersOptions.displayName = 'TableFiltersOptions';

const TableFiltersOptionsResizer = styled(UtilityControl, {
  position: 'absolute',
  bottom: 0,
  left: '50%',
  transform: 'translate(-50%, 50%)',
  zIndex: 1,
  touchAction: 'none',

  '&&': {
    backgroundColor: colors.bgNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
    },
  },
});
TableFiltersOptionsResizer.displayName = 'TableFiltersOptionsResizer';

const TableFiltersControls = styled(ControlGroup, {
  gridArea: 'controls',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '$8',
  padding: `$${tableFiltersVars.padding.ends} $${tableFiltersVars.padding.sides / 2}`,
});
TableFiltersControls.displayName = 'TableFiltersControls';

const TableFiltersContainer = styled('div', {
  display: 'grid',
  width: '100%',

  variants: {
    isNarrow: {
      true: {
        display: 'flex',
        flexWrap: 'wrap',

        [`& > ${TableFiltersControls}`]: {
          width: 'min-content',
          marginLeft: 'auto',
        },

        [`& > ${TableFiltersOptions}`]: {
          order: 1,
          flexBasis: '100%',
        },
      },
      false: {
        gridTemplateColumns: 'min-content 1fr min-content',
        gridTemplateAreas: `
          "views options controls"
        `,
        width: '100%',
      },
    },
  },
});
TableFiltersContainer.displayName = 'TableFiltersContainer';

interface TableFiltersOptionsAreaProps {
  isNarrow: boolean;
  viewGutter: GutterConfig | undefined;
  hasViews: boolean;
  hasControls: boolean;
  children: ReactNode;
}

function TableFiltersOptionsArea({
  isNarrow,
  viewGutter,
  hasViews,
  hasControls,
  children,
}: TableFiltersOptionsAreaProps) {
  const optionsScroll = useOptionsScroll();
  const scrollId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const { visible } = useUtilityVisibility({ containerRef });
  const { usePersistedNarrowMaxHeight } = useTableFiltersEnvironment();
  const [maxHeight, setMaxHeight] = usePersistedNarrowMaxHeight();
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const dragRef = useRef<{ startY: number; startHeight: number; height: number } | null>(null);

  const liveMaxHeight = dragHeight ?? maxHeight;
  const { overflowing, height, scrollHeight } = optionsScroll;
  const atMaxHeight = overflowing || (liveMaxHeight !== null && height >= liveMaxHeight - 1);
  const resizable = isNarrow && (atMaxHeight || dragHeight !== null);
  const resizeCursor = getResizeCursor({
    axis: 'y',
    value: dragHeight ?? height,
    min: NARROW_OPTIONS_MIN_HEIGHT,
    max: scrollHeight,
    tolerance: 1,
  });

  const clampHeight = (value: number) => {
    const contentHeight = optionsScroll.elementRef.current?.scrollHeight ?? Infinity;
    return Math.round(Math.max(NARROW_OPTIONS_MIN_HEIGHT, Math.min(value, contentHeight)));
  };

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    const element = optionsScroll.elementRef.current;
    if (!element || event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const startHeight = element.getBoundingClientRect().height;
    dragRef.current = { startY: event.clientY, startHeight, height: startHeight };
    setDragHeight(startHeight);
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    drag.height = clampHeight(drag.startHeight + event.clientY - drag.startY);
    setDragHeight(drag.height);
  };

  const handlePointerEnd = () => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    setDragHeight(null);
    if (drag.height !== drag.startHeight) setMaxHeight(drag.height);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const element = optionsScroll.elementRef.current;
    if (!element) return;
    const current = element.getBoundingClientRect().height;
    let next: number;
    if (event.key === 'ArrowUp') next = current - NARROW_OPTIONS_ROW_HEIGHT;
    else if (event.key === 'ArrowDown') next = current + NARROW_OPTIONS_ROW_HEIGHT;
    else if (event.key === 'Home') next = NARROW_OPTIONS_MIN_HEIGHT;
    else if (event.key === 'End') next = Infinity;
    else return;
    event.preventDefault();
    setMaxHeight(clampHeight(next));
  };

  return (
    <TableFiltersOptions ref={containerRef}>
      <TableFiltersOptionsScroll
        id={scrollId}
        isNarrow={isNarrow}
        css={isNarrow ? narrowOptionsCSS(viewGutter) : undefined}
        style={isNarrow && liveMaxHeight !== null ? { maxHeight: liveMaxHeight } : undefined}
        hasViews={hasViews}
        hasControls={hasControls}
        ref={optionsScroll.ref}
      >
        {children}
      </TableFiltersOptionsScroll>
      {resizable && (
        <TableFiltersOptionsResizer
          role="separator"
          aria-orientation="horizontal"
          aria-controls={scrollId}
          aria-valuenow={Math.round(height)}
          aria-valuemin={NARROW_OPTIONS_MIN_HEIGHT}
          aria-valuemax={scrollHeight}
          placement="ends"
          icon="drag-horizontal"
          label="Resize filters"
          visible={visible || dragHeight !== null}
          style={{ cursor: resizeCursor }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onLostPointerCapture={handlePointerEnd}
          onKeyDown={handleKeyDown}
          onDoubleClick={() => setMaxHeight(null)}
        />
      )}
      <ResizeCursorShield cursor={dragHeight !== null ? resizeCursor : null} />
    </TableFiltersOptions>
  );
}

type FilterMapVal<RowType> = Omit<
  FilterComponentProps<RowType>,
  'handleFiltersChange' | 'onKeyDown'
> & {
  label: string;
  filter?: FilterComponentFunction<RowType>;
  variant?: FilterVariant;
  options?: FilterOptions<RowType>;
  optionsLoading?: boolean;
  negatable?: boolean;
  dateRangePresets?: boolean;
  pinned?: boolean;
  accessLevel?: AccessLevelProp | null | undefined;
};

function filterModifier<RowType>({ negatable, value }: FilterMapVal<RowType>) {
  if (!negatable) return undefined;
  const { values, negated } = parseMultiSelectFilterValue(value);
  if (values.length === 0) return undefined;
  return negated ? 'is not' : 'is';
}

function TableFilter<RowType>({
  mappedFilter,
  options,
  handleFiltersChange,
  handleFilterValueSelected,
}: {
  mappedFilter: FilterMapVal<RowType>;
  options: FilterOption[] | undefined;
  handleFiltersChange: (id: string, value: FilterValue) => void;
  handleFilterValueSelected?: (id: string) => void;
}) {
  if (mappedFilter?.filter) {
    return mappedFilter.filter({
      ...mappedFilter,
      handleFiltersChange,
      onKeyDown: () => {},
    });
  }

  switch (mappedFilter.variant) {
    case 'select':
      return (
        <TableFiltersFieldSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'multi-select':
      return (
        <TableFiltersFieldMultiSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
        />
      );
    case 'number-range':
      return (
        <TableFiltersFieldNumberRange
          {...mappedFilter}
          value={mappedFilter.value as [number | null, number | null]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'number':
      return (
        <TableFiltersFieldNumber
          {...mappedFilter}
          value={mappedFilter.value as number | null}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'text':
      return (
        <TableFiltersFieldText
          {...mappedFilter}
          value={mappedFilter.value as string}
          onChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'calendar-date-range': {
      const range = parseCalendarDateRange(mappedFilter.value);
      return (
        <DateTimeRangeStorageProvider
          key={JSON.stringify(range)}
          timePeriod={range[0] || range[1] ? TimePeriod.Custom : TimePeriod.OneDay}
          startDate={range[0] ?? undefined}
          endDate={range[1] ?? undefined}
        >
          <TableFiltersFieldDateRange
            {...mappedFilter}
            value={range}
            calendarDates
            handleFiltersChange={handleFiltersChange}
            onValueSelected={
              handleFilterValueSelected
                ? () => handleFilterValueSelected(mappedFilter.id)
                : undefined
            }
            showPresets={mappedFilter.dateRangePresets !== false}
          />
        </DateTimeRangeStorageProvider>
      );
    }
    case 'date-range':
      return (
        <DateTimeRangeStorageProvider>
          <TableFiltersFieldDateRange
            {...mappedFilter}
            value={mappedFilter.value as [string | null, string | null]}
            handleFiltersChange={handleFiltersChange}
            onKeyDown={() => {}}
            onValueSelected={
              handleFilterValueSelected
                ? () => handleFilterValueSelected(mappedFilter.id)
                : undefined
            }
            showPresets={mappedFilter.dateRangePresets !== false}
          />
        </DateTimeRangeStorageProvider>
      );
    default:
      return null;
  }
}

function normalizeFilterOption(option: FilterOption | string): NormalizedFilterOption {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
    };
  }

  if (!option.label) {
    return {
      ...option,
      label: option.value,
    };
  }

  return option as NormalizedFilterOption;
}

function getFilterOptionTextValue(option: NormalizedFilterOption): string {
  if (option.textValue) return option.textValue;
  if (typeof option.label === 'string') return option.label;
  return option.value;
}

function normalizeFilterOptions<RowType>(
  data: RowType[] | undefined,
  filterOptions: FilterOptions<RowType> | undefined,
): NormalizedFilterOption[] | undefined {
  if (!filterOptions) {
    return undefined;
  }

  if (Array.isArray(filterOptions)) {
    return filterOptions.map(normalizeFilterOption);
  }

  const options = filterOptions(data)?.map(normalizeFilterOption) ?? [];
  const map = new Map<string, NormalizedFilterOption>();
  for (const option of options) {
    if (!map.has(option.value)) {
      map.set(option.value, option);
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    sortingCollator.compare(getFilterOptionTextValue(a), getFilterOptionTextValue(b)),
  );
}

function initValForVariant(variant?: FilterVariant): unknown {
  if (variant) {
    switch (variant) {
      case 'select':
        return null;
      case 'multi-select':
        return [];
      case 'text':
        return '';
      case 'number-range':
      case 'date-range':
      case 'calendar-date-range':
        return [null, null];
      case 'number':
        return null;
      case 'boolean':
        return true;
      default:
        return null;
    }
  }
  return null;
}

export function TableFilters<RowType>({
  columns = [],
  data,
  filtersState = [],
  onFiltersChange = () => {},
  onClearFilters,
  isFiltersDirty,
  onResetFilters,
  controls,
  staticFilters,
  isNarrow: isNarrowProp,
  viewGutter,
}: {
  columns?: SharedColumnDef<RowType, any>[];
  data?: RowType[];
  filtersState?: ColumnFiltersState;
  onFiltersChange?: (columnState: ColumnFiltersState) => void;
  onClearFilters?: React.MouseEventHandler<HTMLButtonElement>;
  isFiltersDirty?: boolean;
  onResetFilters?: () => void;
  controls?: ReactNode;
  staticFilters?: ReactNode;
  isNarrow?: boolean;
  viewGutter?: GutterConfig;
}) {
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && (col.filterFn || col.filter) && col.filterVariant) {
        const label = getColumnFilterLabel(col) ?? '';
        const value = filtersState?.find((filter) => filter.id === col.id)?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data,
          filter: col.filter,
          options: col.filterOptions,
          optionsLoading: col.filterOptionsLoading,
          variant: col.filterVariant,
          negatable: col.filterNegatable,
          dateRangePresets: col.filterDateRangePresets,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = getColumnFilterLabel(col);
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = filtersState?.find((filter) => filter.id === sublineId)?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          optionsLoading: col.sublineFilterOptionsLoading,
          variant: col.sublineFilterVariant,
        });
      }
    }

    return filterList;
  }, [columns, filtersState, data]);

  const filtersMap = useMemo(
    () => new Map<string, FilterMapVal<RowType>>(filters.map((f) => [f.id, f])),
    [filters],
  );

  const filterOptions = useMemo(() => {
    const optionsMap = new Map<string, SelectedFiltersDropdownOption>();

    for (const col of columns) {
      // Add main column filter option
      if ((col?.filterFn || col?.filter) && col?.filterVariant && col.id) {
        const label = getColumnFilterLabel(col) ?? '';
        const group = col.meta?.group ?? null;

        optionsMap.set(col.id, {
          value: col.id,
          label,
          group,
          accessLevel: col.meta?.accessLevel,
        });
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = getColumnFilterLabel(col);
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;

        optionsMap.set(sublineId, {
          value: sublineId,
          label,
          group,
        });
      }
    }
    return Array.from(optionsMap.values());
  }, [columns]);

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = filtersState.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          filtersState.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...filtersState, { id, value }];
      }
      onFiltersChange(newFilters);
    },
    [filtersState, onFiltersChange],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      // Find newly added filters
      const currentFilterIds = new Set(filtersState?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = filtersState?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Open newly added filters, and forget removed ones so a filter added back from outside the
      // bar (e.g. a row menu) doesn't reopen.
      setOpenFilters(
        (prev) => new Set([...[...prev].filter((id) => ids.includes(id)), ...newlyAddedFilters]),
      );

      onFiltersChange(newFilters);
    },
    [filtersMap, filtersState, onFiltersChange],
  );

  const handleClearFilters = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onFiltersChange([]);
      setOpenFilters(new Set());
      if (onClearFilters) {
        onClearFilters(e);
      }
    },
    [onFiltersChange, onClearFilters],
  );

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        filtersState?.filter((filter) => filter.id !== id).map((filter) => filter.id) ?? [];
      handleSelectedFiltersChange(filteredIds);
    },
    [filtersState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const handleFilterValueSelected = useCallback((id: string) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const filtersStateWithOptions = useMemo(
    () =>
      filtersState.map((filter) => {
        const mappedFilter = filtersMap.get(filter.id);
        const finalOptions = normalizeFilterOptions(data, mappedFilter?.options);

        return {
          ...filter,
          mappedFilter,
          finalOptions,
        };
      }),
    [data, filtersMap, filtersState],
  );

  const updateFilterValue = useEffectEvent((columnID: string, value: FilterValue) => {
    handleFiltersChange(columnID, value);
  });

  useEffect(() => {
    for (const { id, finalOptions, value, mappedFilter } of filtersStateWithOptions) {
      if (
        mappedFilter?.optionsLoading ||
        !finalOptions ||
        !(Array.isArray(value) || isNegatedFilterValue(value))
      ) {
        continue;
      }
      const { values, negated } = parseMultiSelectFilterValue(value);
      const optionsMap = new Map(finalOptions.map((opt) => [opt.value, opt]));
      const validValues = values
        .map((v) => optionsMap.get(v))
        .filter(isDefined)
        .map((opt) => opt.value);
      if (validValues.length !== values.length) {
        updateFilterValue(id, multiSelectFilterValue(validValues, negated));
      }
    }
  }, [filtersMap, columns, filtersStateWithOptions]);

  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && filtersState?.length > 0);
  const hasControls = Boolean(controls || hasFilters);
  const canAddFilter = filtersState.length < filterOptions.length;

  const [ref, { width }] = useMeasure();
  const isNarrow = isNarrowProp ?? Boolean(width && width < 700);

  const showNarrowAddFilter = isNarrow && hasColumns && filters.length > 0 && canAddFilter;
  const hasViews = Boolean(staticFilters) || showNarrowAddFilter;

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      {hasViews && (
        <TableFiltersViews
          relation="separate"
          size="small"
          css={outerGutterCSS('start', viewGutter)}
        >
          {staticFilters}
          {staticFilters && hasColumns && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
          {showNarrowAddFilter && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={filtersState.map((filter) => filter.id)}
              setSelectedFilters={handleSelectedFiltersChange}
            />
          )}
        </TableFiltersViews>
      )}
      {hasColumns && filters.length > 0 && (!isNarrow || hasFilters) && (
        <TableFiltersOptionsArea
          isNarrow={isNarrow}
          viewGutter={viewGutter}
          hasViews={hasViews}
          hasControls={hasControls}
        >
          <ClearAccessLevelContext>
            {filtersStateWithOptions.map(({ mappedFilter, finalOptions: options }) => {
              if (mappedFilter) {
                const sharedTokenProps = {
                  key: mappedFilter.id,
                  handle: mappedFilter.label,
                  modifier: filterModifier(mappedFilter),
                  value: (
                    <DisplayForFilterValue
                      value={mappedFilter.value}
                      options={options}
                      variant={mappedFilter.variant}
                      loading={mappedFilter.optionsLoading}
                    />
                  ),
                  onTokenClear: () => handleClearFilter(mappedFilter.id),
                  modified: isFilterApplied(mappedFilter.value),
                };

                return (
                  <TableFiltersToken
                    {...sharedTokenProps}
                    changeable
                    isOpen={openFilters.has(mappedFilter.id)}
                    onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                  >
                    <TableFilter
                      mappedFilter={mappedFilter}
                      options={options!}
                      handleFiltersChange={handleFiltersChange}
                      handleFilterValueSelected={handleFilterValueSelected}
                    />
                  </TableFiltersToken>
                );
              }
              return null;
            })}
          </ClearAccessLevelContext>
          {!isNarrow && canAddFilter && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={filtersState.map((filter) => filter.id)}
              setSelectedFilters={handleSelectedFiltersChange}
            />
          )}
        </TableFiltersOptionsArea>
      )}
      {hasControls && (
        <ClearAccessLevelContext>
          <TableFiltersControls
            size="small"
            relation="separate"
            css={outerGutterCSS('end', viewGutter)}
          >
            {hasFilters && (
              <Button
                onClick={handleClearFilters}
                arrangement="hidden-label"
                appearance="muted"
                icon="trash-can"
                size="small"
                variant="neutral"
              >
                Clear filters
              </Button>
            )}
            {isFiltersDirty && onResetFilters && (
              <Button
                onClick={onResetFilters}
                arrangement="hidden-label"
                appearance="muted"
                icon="reset"
                size="small"
                variant="neutral"
              >
                Reset filters
              </Button>
            )}
            {hasFilters && controls && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {controls && controls}
          </TableFiltersControls>
        </ClearAccessLevelContext>
      )}
    </TableFiltersContainer>
  );
}

// Enhanced interface for TableState integration
function TableSort<RowType, C extends SharedColumnDef<RowType, any>>({
  columns,
  tableState,
}: {
  columns: C[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
}) {
  const sortableColumns = useMemo(
    () =>
      columns.filter(
        (col): col is C & { id: string } =>
          typeof col.id === 'string' &&
          (typeof col.header === 'string' || typeof col.meta?.label === 'string') &&
          col.enableSorting !== false,
      ),
    [columns],
  );

  if (sortableColumns.length === 0) return null;

  const currentSort = tableState.sortingState[0];
  const activeColumn = sortableColumns.find((c) => c.id === currentSort?.id) ?? sortableColumns[0];
  const isDesc = currentSort?.desc ?? false;

  return (
    <DropdownMenu>
      <DropdownMenuButton appearance="muted" arrangement="hidden-label" icon="arrows-up-down">
        Sort
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        <DropdownMenuRadioGroup
          label="Sort by"
          value={activeColumn.id}
          onValueChange={(newColumnId) => {
            tableState.setSortingState([{ id: newColumnId, desc: isDesc }]);
          }}
        >
          {sortableColumns.map((col) => (
            <DropdownMenuRadioItem
              key={col.id}
              value={col.id}
              label={(col.meta?.label ?? col.header) as string}
            />
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuRadioGroup
          label="Direction"
          value={isDesc ? 'desc' : 'asc'}
          onValueChange={(dir) => {
            tableState.setSortingState([{ id: activeColumn.id, desc: dir === 'desc' }]);
          }}
        >
          <DropdownMenuRadioItem value="asc" label="Ascending" icon="arrow-up" />
          <DropdownMenuRadioItem value="desc" label="Descending" icon="arrow-down" />
        </DropdownMenuRadioGroup>
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

interface TableFiltersEnhancedBaseProps<RowType, C extends SharedColumnDef<RowType, any>> {
  columns?: C[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
  /** Disables all filter fields. */
  disabled?: boolean;
  staticFilters?: ReactNode;
  controls?: ReactNode;
  overflowActions?: TableFiltersActions[];
  multiSelectActions?: ReactNode;
  /** When true, shows a sort dropdown in the filter bar. Intended for headerless layouts like AutoBlocks. */
  enableSortMenu?: boolean;
  /** When true, shows a customization menu in the filter bar. Intended for headerless layouts like AutoBlocks. */
  enableCustomizationMenu?: boolean;
  /** When true, column reordering is disabled (no drag handles in Columns dropdown). */
  disableColumnOrder?: boolean;
  /** When true, options in the "Add a filter…" dropdown are sorted alphabetically within each group. */
  sortFilterOptions?: boolean;
  /** When true, forces the stacked narrow layout instead of deriving it from the measured width. */
  isNarrow?: boolean;
  /** gutter for the views and controls cells' outer edges. defaults to the application gutter. */
  viewGutter?: GutterConfig;
  /** Enables the CSV/JSON dialog; existing CSV export behavior is the default. */
  enableExportDialog?: boolean;
  /** Disables both the existing CSV action and the export dialog. */
  disableCSVExport?: boolean;
}

interface ClientTableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>>
  extends TableFiltersEnhancedBaseProps<RowType, C> {
  data?: RowType[];
  /** Provides all-results export without changing how filters are rendered. */
  table?: Table<RowType>;
  hideGlobalFilter?: boolean;
  /** When true, shows a presets dropdown in the filter bar to save and select filter presets. */
  enablePresets?: boolean;
  /** Shortcut priority for search (0 = ⌘F, 1 = ⌘G). Lower = primary. */
  shortcutPriority?: number;
}

interface ServerTableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>>
  extends TableFiltersEnhancedBaseProps<RowType, C> {
  table: Table<RowType>;
}

type TableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>> =
  | (ClientTableFiltersEnhancedProps<RowType, C> & { serverSide?: false })
  | (ServerTableFiltersEnhancedProps<RowType, C> & { serverSide: true });

function ClientTableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  columns = [],
  data,
  table,
  tableState,
  controls,
  staticFilters,
  multiSelectActions,
  hideGlobalFilter,
  disableColumnOrder = false,
  enableCustomizationMenu = true,
  enableSortMenu = false,
  enablePresets = true,
  shortcutPriority,
  enableExportDialog = false,
  disableCSVExport = false,
  overflowActions,
  sortFilterOptions = false,
  disabled,
  viewGutter,
}: ClientTableFiltersEnhancedProps<RowType, C>) {
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());
  const tableExport = useTableExport({ tableState, table });

  const resolvedData = data ?? tableState.tableRef?.current?.data;

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && col.filterVariant) {
        const label = getColumnFilterLabel(col) ?? '';
        const value = tableState.columnFilters?.find((filter) => filter.id === col.id)
          ?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data: resolvedData,
          filter: col.filter,
          options: col.filterOptions,
          optionsLoading: col.filterOptionsLoading,
          variant: col.filterVariant,
          negatable: col.filterNegatable,
          dateRangePresets: col.filterDateRangePresets,
          pinned: col.meta?.filterPinned,
          accessLevel: col.meta?.accessLevel,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = getColumnFilterLabel(col);
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = tableState.columnFilters?.find((filter) => filter.id === sublineId)
          ?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data: resolvedData,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          optionsLoading: col.sublineFilterOptionsLoading,
          variant: col.sublineFilterVariant,
          pinned: col.meta?.sublineFilterPinned,
          accessLevel: col.meta?.accessLevel,
        });
      }
    }

    return filterList;
  }, [columns, tableState.columnFilters, resolvedData]);

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterMapVal<RowType>>();
    for (const f of filters) {
      map.set(f.id, f);
    }
    return map;
  }, [filters]);

  const filterOptions = useMemo(() => {
    const optionsMap = new Map<
      string,
      {
        variant: FilterVariant;
        value: string;
        label: string;
        group?: string | null;
        pinned?: boolean;
        accessLevel?: AccessLevelProp | null | undefined;
      }
    >();

    for (const col of columns) {
      // Add main column filter option
      if (col?.filterVariant && col.id) {
        const label = getColumnFilterLabel(col) ?? '';
        const group = col.meta?.group ?? null;

        optionsMap.set(col.id, {
          variant: col.filterVariant,
          value: col.id,
          label,
          group,
          pinned: col?.meta?.filterPinned,
          accessLevel: col?.meta?.accessLevel,
        });
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = getColumnFilterLabel(col);
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;

        optionsMap.set(sublineId, {
          variant: col.sublineFilterVariant,
          value: sublineId,
          label,
          group,
          pinned: col?.meta?.sublineFilterPinned,
          accessLevel: col?.meta?.accessLevel,
        });
      }
    }
    return Array.from(optionsMap.values());
  }, [columns]);

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = tableState.columnFilters?.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          tableState.columnFilters?.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...(tableState.columnFilters ?? []), { id, value }];
      }

      const filterIDs = new Set(newFilters.map((f) => f.id));
      const pinnedFiltersToAdd = [];
      // check for pinned filters, add if they are missing
      for (const filterOption of filterOptions) {
        if (!filterOption.pinned) {
          continue;
        }

        if (filterIDs.has(filterOption.value)) {
          continue;
        }

        pinnedFiltersToAdd.push({
          id: filterOption.value,
          value: initValForVariant(filterOption.variant),
        });
      }

      if (pinnedFiltersToAdd.length > 0) {
        newFilters.splice(0, 0, ...pinnedFiltersToAdd);
      }

      tableState.setColumnFilters(newFilters);
    },
    [filterOptions, tableState],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      // Find newly added filters
      const currentFilterIds = new Set(tableState.columnFilters?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = tableState.columnFilters?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Open newly added filters, and forget removed ones so a filter added back from outside the
      // bar (e.g. a row menu) doesn't reopen.
      setOpenFilters(
        (prev) => new Set([...[...prev].filter((id) => ids.includes(id)), ...newlyAddedFilters]),
      );

      tableState.setColumnFilters(newFilters);
    },
    [filtersMap, tableState],
  );

  const handleExportToCSV = useCallback(() => {
    tableState.tableRef?.current?.exportToCSV();
  }, [tableState.tableRef]);

  const handleClearFilters = useCallback(() => {
    tableState.setColumnFilters([]);
    setOpenFilters(new Set());
  }, [tableState]);

  const handleResetFilters = useCallback(() => {
    tableState.resetFilters();
    setOpenFilters(new Set());
  }, [tableState]);

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        tableState.columnFilters?.filter((filter) => filter.id !== id).map((filter) => filter.id) ??
        [];
      handleSelectedFiltersChange(filteredIds);
    },
    [tableState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const filtersStateWithOptions = useMemo(
    () =>
      tableState.columnFilters.map((filter) => {
        const mappedFilter = filtersMap.get(filter.id);
        const finalOptions = normalizeFilterOptions(
          data ?? tableState.tableRef?.current?.data,
          mappedFilter?.options,
        );

        return {
          ...filter,
          mappedFilter,
          finalOptions,
        };
      }),
    [data, filtersMap, tableState.columnFilters, tableState.tableRef],
  );

  const updateFilterValue = useEffectEvent((columnID: string, value: FilterValue) => {
    handleFiltersChange(columnID, value);
  });

  useEffect(() => {
    for (const { id, finalOptions, value, mappedFilter } of filtersStateWithOptions) {
      if (
        mappedFilter?.optionsLoading ||
        !finalOptions ||
        !(Array.isArray(value) || isNegatedFilterValue(value))
      ) {
        continue;
      }
      const { values, negated } = parseMultiSelectFilterValue(value);
      const optionsMap = new Map(finalOptions.map((opt) => [opt.value, opt]));
      const validValues = values
        .map((v) => optionsMap.get(v))
        .filter(isDefined)
        .map((opt) => opt.value);
      if (validValues.length !== values.length) {
        updateFilterValue(id, multiSelectFilterValue(validValues, negated));
      }
    }
  }, [filtersMap, columns, filtersStateWithOptions]);

  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && tableState.columnFilters?.length > 0);
  const [ref, { width }] = useMeasure();

  const isNarrow = Boolean(width && width < 700);

  const columnsForTableColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );

  const hasTableCustomization = Boolean(enableCustomizationMenu && columns.length > 0);
  const hasTableSorting = Boolean(enableSortMenu && columns.length > 0);
  const hasStartControls = Boolean(hasTableCustomization || hasTableSorting);
  const hasOverflowActions =
    (overflowActions?.length ?? 0) > 0 || (tableState.tableRef && !disableCSVExport);
  const hasControls = Boolean(
    controls ||
      hasFilters ||
      hasOverflowActions ||
      !hideGlobalFilter ||
      !tableState.isFiltersDefault,
  );
  const canAddFilter = tableState.columnFilters.length < filterOptions.length;
  const showSelectedFiltersDropdown = Boolean(
    filters.length > 0 && (tableState.columnFilters.length === 0 || (isNarrow && canAddFilter)),
  );
  const showTableFiltersViews = Boolean(
    multiSelectActions ||
      enablePresets ||
      hasStartControls ||
      staticFilters ||
      showSelectedFiltersDropdown,
  );

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      {showTableFiltersViews && (
        <TableFiltersViews
          relation="separate"
          size="small"
          css={outerGutterCSS('start', viewGutter)}
        >
          {multiSelectActions}
          {enablePresets && tableState.presets && <TablePresets presets={tableState.presets} />}
          {hasStartControls && (
            <ControlGroup relation="joined" size="small">
              {hasTableCustomization && (
                <TableColumnsEnhanced<RowType>
                  columns={columnsForTableColumns}
                  tableState={tableState}
                />
              )}
              {hasTableSorting && <TableSort columns={columns} tableState={tableState} />}
            </ControlGroup>
          )}
          {staticFilters}
          {hasStartControls &&
            hasColumns &&
            filters.length > 0 &&
            (!isNarrow || showSelectedFiltersDropdown) && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
          {showSelectedFiltersDropdown && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
              setSelectedFilters={handleSelectedFiltersChange}
              columnOrder={tableState.columnOrder}
              onClearFilters={handleClearFilters}
              sortOptions={sortFilterOptions}
              disabled={disabled}
            />
          )}
        </TableFiltersViews>
      )}
      {hasColumns && filters.length > 0 && tableState.columnFilters.length > 0 && (
        <TableFiltersOptionsArea
          isNarrow={isNarrow}
          viewGutter={viewGutter}
          hasViews
          hasControls={hasControls}
        >
          <ClearAccessLevelContext>
            {filtersStateWithOptions.map(({ mappedFilter, finalOptions: options }) => {
              if (!mappedFilter) return null;

              const sharedTokenProps = {
                handle: mappedFilter.label,
                modifier: filterModifier(mappedFilter),
                value: (
                  <DisplayForFilterValue
                    value={mappedFilter.value}
                    options={options}
                    variant={mappedFilter.variant}
                    loading={mappedFilter.optionsLoading}
                  />
                ),
                onTokenClear: () => handleClearFilter(mappedFilter.id),
                modified: isFilterApplied(mappedFilter.value),
                dismissable: !mappedFilter.pinned,
              } satisfies Partial<TableFiltersTokenProps>;

              if (mappedFilter.variant === 'boolean') {
                // A boolean column with its own filter component gets a hybrid token: the
                // toggle answers from the bar, and the dropdown holds the component's
                // refinements of the toggled-on state (which replace the bare boolean).
                if (mappedFilter.filter) {
                  return (
                    <TableFiltersToken
                      {...sharedTokenProps}
                      key={mappedFilter.id}
                      variant="toggle-dropdown"
                      onToggleClick={() => {
                        const turningOn = mappedFilter.value === false;
                        // Toggling off unmounts the refinement dropdown; forget its open state
                        // so switching back on doesn't pop it open unasked.
                        if (!turningOn) handleFilterOpenChange(mappedFilter.id, false);
                        handleFiltersChange(mappedFilter.id, turningOn);
                      }}
                      toggleSelected={mappedFilter.value !== false}
                      // An empty refinement renders as nothing, which would defeat the 'All'
                      // fallback; only a range with an end in it earns the display.
                      value={
                        Array.isArray(mappedFilter.value) &&
                        mappedFilter.value.some((end) => end != null)
                          ? sharedTokenProps.value
                          : null
                      }
                      changeable
                      isOpen={openFilters.has(mappedFilter.id)}
                      onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                      disabled={disabled}
                    >
                      <TableFilter
                        mappedFilter={mappedFilter}
                        options={options}
                        handleFiltersChange={handleFiltersChange}
                      />
                    </TableFiltersToken>
                  );
                }

                return (
                  <TableFiltersToken
                    {...sharedTokenProps}
                    key={mappedFilter.id}
                    variant="toggle"
                    onToggleClick={() =>
                      handleFiltersChange(mappedFilter.id, !(mappedFilter.value as boolean))
                    }
                    toggleSelected={mappedFilter.value as boolean}
                  />
                );
              }

              return (
                <TableFiltersToken
                  {...sharedTokenProps}
                  key={mappedFilter.id}
                  changeable
                  isOpen={openFilters.has(mappedFilter.id)}
                  onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                  disabled={disabled}
                >
                  <TableFilter
                    mappedFilter={mappedFilter}
                    options={options}
                    handleFiltersChange={handleFiltersChange}
                  />
                </TableFiltersToken>
              );
            })}
          </ClearAccessLevelContext>
          {!isNarrow && canAddFilter && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
              setSelectedFilters={handleSelectedFiltersChange}
              columnOrder={tableState.columnOrder}
              onClearFilters={handleClearFilters}
              sortOptions={sortFilterOptions}
              disabled={disabled}
            />
          )}
        </TableFiltersOptionsArea>
      )}
      {hasControls && (
        <ClearAccessLevelContext>
          <TableFiltersControls
            size="small"
            relation="separate"
            css={outerGutterCSS('end', viewGutter)}
          >
            {hasFilters && (
              <Button
                onClick={handleClearFilters}
                appearance="muted"
                arrangement="hidden-label"
                icon="trash-can"
                size="small"
                variant="neutral"
                disabled={disabled}
              >
                Clear filters
              </Button>
            )}
            {!tableState.isFiltersDefault && (
              <Button
                onClick={handleResetFilters}
                arrangement="hidden-label"
                appearance="muted"
                icon="reset"
                size="small"
                variant="neutral"
              >
                Reset filters
              </Button>
            )}
            {hasFilters && controls && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {controls}
            {enableExportDialog && tableExport.dialog}
            {hasOverflowActions && (
              <DropdownMenu>
                <DropdownMenuButton
                  appearance="muted"
                  arrangement="hidden-label"
                  icon="overflow-horizontal"
                  variant="secondary"
                >
                  More actions
                </DropdownMenuButton>
                <DropdownMenuPopover>
                  <TableFiltersActionsMenu actions={overflowActions} />
                  {tableState.tableRef &&
                    !disableCSVExport &&
                    (enableExportDialog ? (
                      tableExport.menu
                    ) : (
                      <DropdownMenuGroup>
                        <DropdownMenuItem
                          onClick={handleExportToCSV}
                          icon="export"
                          description={
                            tableState.isServerSidePaginated
                              ? 'Exports the current page of data only.'
                              : undefined
                          }
                          disabled={tableState.numRows === 0}
                        >
                          Export CSV
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    ))}
                </DropdownMenuPopover>
              </DropdownMenu>
            )}
            {!hideGlobalFilter && (
              <KeyBoundSearchInput
                searchParamStateKey={`${tableState.storageKey}-filter`}
                shortcutPriority={shortcutPriority}
                aria-label="Filter"
                value={tableState.globalFilter}
                onChange={tableState.setGlobalFilter}
                disabled={disabled}
              />
            )}
          </TableFiltersControls>
        </ClearAccessLevelContext>
      )}
    </TableFiltersContainer>
  );
}

export interface SortColumnDefinition {
  name: string;
  value: string;
}

interface ServerSideTableSortProps<RowType> {
  table: Table<RowType>;
}

function ServerSideTableSort<RowType>({ table }: ServerSideTableSortProps<RowType>) {
  if (!table.sortDefinitions || table.sortDefinitions.length === 0) {
    return null;
  }

  const currentSort = table.getSortOrder();

  return (
    <DropdownMenu>
      <DropdownMenuButton appearance="muted" arrangement="hidden-label" icon="arrows-up-down">
        Sort
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        <DropdownMenuRadioGroup
          label="Sort by"
          value={currentSort.column}
          onValueChange={(newColumnId) => {
            table.setSortColumn(newColumnId);
          }}
        >
          {table.sortDefinitions?.map((definition) => (
            <DropdownMenuRadioItem
              key={definition.value}
              value={definition.value}
              label={definition.name}
            />
          )) ?? null}
        </DropdownMenuRadioGroup>
        <DropdownMenuRadioGroup
          label="Direction"
          value={currentSort.orderBy}
          onValueChange={(dir) => {
            if (isTableSortOrder(dir)) {
              table.setSortOrderType(dir);
            }
          }}
        >
          <DropdownMenuRadioItem
            value={TableSortOrderType.Ascending}
            label="Ascending"
            icon="arrow-up"
          />
          <DropdownMenuRadioItem
            value={TableSortOrderType.Descending}
            label="Descending"
            icon="arrow-down"
          />
        </DropdownMenuRadioGroup>
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

function ServerTableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  multiSelectActions,
  columns = [],
  enableCustomizationMenu = true,
  enableSortMenu = false,
  staticFilters,
  disableColumnOrder = false,
  controls,
  overflowActions,
  table,
  tableState,
  sortFilterOptions = false,
  disabled,
  isNarrow: isNarrowProp,
  viewGutter,
  enableExportDialog = false,
  disableCSVExport = false,
}: ServerTableFiltersEnhancedProps<RowType, C>) {
  const tableExport = useTableExport({ tableState, table });
  const [ref, { width }] = useMeasure();
  const isNarrow = isNarrowProp ?? (width !== null && width < 700);
  const hasTableCustomization = enableCustomizationMenu && columns.length > 0;
  const hasTableSorting = enableSortMenu && columns.length > 0;
  const hasStartControls = hasTableCustomization || hasTableSorting;
  const hasColumns = columns?.length !== 0;
  const hasOverflowActions =
    (overflowActions?.length ?? 0) > 0 ||
    (enableExportDialog && tableState.tableRef && !disableCSVExport);
  const hasFilters = hasColumns && table.filters.length > 0;
  const hasControls = !!controls || hasFilters || hasOverflowActions;
  const orderedColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );
  const filterOptions: SelectedFiltersDropdownOption[] = useMemo(() => {
    if (!table.filterDefinitions) {
      return [];
    }
    return table.filterDefinitions.map((definition) => ({
      value: definition.id,
      label: definition.label,
      accessLevel: definition.accessLevel,
    }));
  }, [table.filterDefinitions]);
  const handleSelectedFilters = useCallback(
    (ids: string[]) => {
      table.setFilters(
        ids
          .map<Filter | undefined>((id) => {
            const filter = table.getFilterByID(id);
            if (filter) {
              return filter;
            }
            const definition = table.getFilterDefinition(id);
            if (!definition) {
              return undefined;
            }
            switch (definition.variant) {
              case TableFilterVariant.Bool:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: true,
                };
              case TableFilterVariant.Text:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: '',
                };
              case TableFilterVariant.Number:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: 0,
                };
              case TableFilterVariant.Select:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: '',
                };
              case TableFilterVariant.DateRange:
                return {
                  key: definition.id,
                  type: definition.variant,
                  start: null,
                  end: null,
                };
              case TableFilterVariant.NumberRange:
                return {
                  key: definition.id,
                  type: definition.variant,
                  start: null,
                  end: null,
                };
              case TableFilterVariant.MultiSelect:
                return {
                  key: definition.id,
                  type: definition.variant,
                  values: [],
                };
              default:
                return undefined;
            }
          })
          .filter((filter) => !!filter),
      );
    },
    [table],
  );
  const handleClearFilters = useCallback(() => {
    table.setFilters([]);
  }, [table]);
  const canAddFilter = table.filters.length < filterOptions.length;
  const showSelectedFiltersDropdown =
    filterOptions.length > 0 && (table.filters.length === 0 || (isNarrow && canAddFilter));

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      <TableFiltersViews relation="separate" size="small" css={outerGutterCSS('start', viewGutter)}>
        {multiSelectActions && multiSelectActions}
        {hasStartControls && (
          <ControlGroup relation="joined" size="small">
            {hasTableCustomization && (
              <TableColumnsEnhanced<RowType> columns={orderedColumns} tableState={tableState} />
            )}
            {hasTableSorting && <ServerSideTableSort table={table} />}
          </ControlGroup>
        )}
        {staticFilters && staticFilters}
        {hasColumns &&
          table.filterDefinitions &&
          table.filterDefinitions.length > 0 &&
          (!isNarrow || showSelectedFiltersDropdown) && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
        {showSelectedFiltersDropdown && (
          <SelectedFiltersDropdown
            options={filterOptions}
            selectedFilters={table.filters.map((filter) => filter.key)}
            setSelectedFilters={handleSelectedFilters}
            loading={table.filterDefinitions === undefined}
            onClearFilters={handleClearFilters}
            sortOptions={sortFilterOptions}
          />
        )}
      </TableFiltersViews>
      {hasColumns &&
        table.filters.length > 0 &&
        table.filterDefinitions &&
        table.filterDefinitions.length > 0 && (
          <TableFiltersOptionsArea
            isNarrow={isNarrow}
            viewGutter={viewGutter}
            hasViews
            hasControls={hasControls}
          >
            <ClearAccessLevelContext>
              {table.filters.map((filter) => (
                <ServerTableFiltersToken key={filter.key} table={table} filter={filter} />
              ))}
            </ClearAccessLevelContext>
            {!isNarrow && canAddFilter && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={table.filters.map((filter) => filter.key)}
                setSelectedFilters={handleSelectedFilters}
                loading={table.filterDefinitions === undefined}
                onClearFilters={handleClearFilters}
                sortOptions={sortFilterOptions}
              />
            )}
          </TableFiltersOptionsArea>
        )}
      {hasControls && (
        <ClearAccessLevelContext>
          <TableFiltersControls
            size="small"
            relation="separate"
            css={outerGutterCSS('end', viewGutter)}
          >
            {hasFilters && (
              <Button
                onClick={handleClearFilters}
                appearance="muted"
                arrangement="hidden-label"
                icon="trash-can"
                size="small"
                variant="neutral"
                disabled={disabled}
              >
                Clear filters
              </Button>
            )}
            {hasFilters && controls && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {controls && controls}
            {enableExportDialog && tableExport.dialog}
            {hasOverflowActions && (
              <DropdownMenu>
                <DropdownMenuButton
                  appearance="muted"
                  arrangement="hidden-label"
                  icon="overflow-horizontal"
                  variant="secondary"
                >
                  More actions
                </DropdownMenuButton>
                <DropdownMenuPopover>
                  <TableFiltersActionsMenu actions={overflowActions} />
                  {enableExportDialog &&
                    tableState.tableRef &&
                    !disableCSVExport &&
                    tableExport.menu}
                </DropdownMenuPopover>
              </DropdownMenu>
            )}
          </TableFiltersControls>
        </ClearAccessLevelContext>
      )}
    </TableFiltersContainer>
  );
}

// Enhanced component that works with TableState
export function TableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>(
  props: TableFiltersEnhancedProps<RowType, C>,
) {
  if (!props.serverSide) {
    return <ClientTableFiltersEnhanced {...props} />;
  }
  return <ServerTableFiltersEnhanced {...props} />;
}
